<template>
  <div data-test-id="student-create-pass-form">
    <span class="text-black-50 text-value-lg mb-2 d-inline-block"
      >Departing From:</span
    >
    <LazyLoadSelect
      data-test-id="stu-pass-fromid"
      v-model="form.from"
      :classes="[
        {
          'is-invalid':
            v$.form.from.$error ||
            (responseMessage.errors && responseMessage.errors['from_id'])
        }
      ]"
      :is-multiple="false"
      :selected="form.from"
      type="combined"
      placeholder="Teacher/Location"
      @changed="
        (value) => {
          form.from = value
        }
      "
    >
      <template #footer>
        <div
          v-if="v$.form.$error && v$.form.from.required.$invalid"
          class="invalid-feedback"
        >
          This field is required
        </div>
        <div
          v-if="responseMessage.errors && responseMessage.errors['from_id']"
          class="invalid-feedback"
        >
          {{ responseMessage.errors["from_id"][0] }}
        </div>
      </template>
    </LazyLoadSelect>

    <FavoriteCarousel
      data-test-id="student-from-favorites"
      :key="'departing'"
      :selected="v$.form.from.$model"
      :is-editable="true"
      :action="
        (elem) => {
          form.from = elem
        }
      "
      help-center-key="to_add_favorite_teacher_txt"
      :isvalid="checkIfValid('from')"
      class="mb-3"
      section="departing"
    />
    <hr />
    <span class="text-black-50 text-value-lg mb-2 d-inline-block"
      >Destination:</span
    >
    <LazyLoadSelect
      data-test-id="stu-pass-toid"
      v-model="form.to"
      :classes="[
        {
          'is-invalid':
            v$.form.to.$error ||
            (responseMessage.errors && responseMessage.errors['to_id'])
        }
      ]"
      :is-multiple="false"
      :selected="form.to"
      type="combined"
      placeholder="Teacher/Location"
      :force-clear-cache="true"
      @changed="(value) => onChangeEvent(value)"
    >
      <template #footer>
        <div
          v-if="form.to && form.to.value && form.to.value.unavailability"
          class="invalid-feedback d-flex flex-wrap fw-bold"
        >
          This destination is unavailable until
          <span
            class="mx-1"
            v-html="
              $helpers.getHTMLDateTime(
                form.to.value.unavailability.to_date,
                'MMMM D, YYYY',
                'h:mm A'
              ) + '.'
            "
          />
          <span v-if="form.to.value.unavailability.comment"
            >Note: {{ form.to.value.unavailability.comment }}</span
          >
        </div>
        <div
          v-if="v$.form.$error && v$.form.to.required.$invalid"
          class="invalid-feedback"
        >
          This field is required
        </div>
        <div
          v-if="
            v$.form.$error &&
            v$.form.to.notSameAsFrom.$invalid &&
            !v$.form.to.required.$invalid
          "
          class="invalid-feedback"
        >
          Start point and destination point should not be the same.
        </div>
        <div
          v-if="responseMessage.errors && responseMessage.errors['to_id']"
          class="invalid-feedback"
        >
          {{ responseMessage.errors["to_id"][0] }}
        </div>
      </template>
    </LazyLoadSelect>

    <FavoriteCarousel
      data-test-id="student-to-favorites"
      :key="'destination'"
      :selected="v$.form.to.$model"
      :is-editable="true"
      :show-unavailables="true"
      :action="
        (elem) => {
          onChangeEvent(elem)
        }
      "
      :isvalid="checkIfValid('to') || !isCapacityReached"
      section="destination"
    />

    <CAlert v-if="toCapacityReached" class="mt-0" color="danger" :show="true">
      Maximum capacity of the location has been reached. Please try again when a
      student returns.
    </CAlert>
    <!-- v-if="form.to.hasOwnProperty('capacity') && form.to.capacity && form.to.capacity.capacity_reached"  -->
    <textarea
      data-test-id="stu-pass-comment"
      maxlength="250"
      v-if="
        form &&
        form.to &&
        form.to.value &&
        form.to.value.comment_type != 'Hidden'
      "
      class="form-control mt-3"
      :placeholder="
        form.to.value.comment_type == 'Optional'
          ? 'Reason for pass (optional)'
          : 'Reason for pass (required)'
      "
      rows="5"
      :class="{
        'is-invalid':
          (v$.form.$error && v$.form.reason.$invalid) ||
          (responseMessage.errors && responseMessage.errors['comment'])
      }"
      v-model="v$.form.reason.$model"
    />
    <div
      v-if="
        form &&
        form.to &&
        form.to.value &&
        form.to.value.comment_type != 'Hidden'
      "
      class="text-end mt-1"
    >
      250 characters max
    </div>
    <div
      v-if="v$.form.$error && v$.form.reason.mandatory.$invalid"
      class="invalid-feedback"
    >
      Reason for pass is required
    </div>
    <div
      v-if="responseMessage.errors && responseMessage.errors['comment']"
      class="invalid-feedback"
    >
      {{ responseMessage.errors["comment"][0] }}
    </div>

    <CAlert
      data-test-id="create-pass-form-alert"
      v-if="responseMessage.message"
      class="mt-3"
      :color="responseMessage.type"
      :show="!!responseMessage.message"
    >
      {{ responseMessage.message }}
    </CAlert>

    <div class="form-group form-actions mt-2 text-center">
      <div
        :data-test-id="
          isLatePass
            ? `create-late-pass-form-submit`
            : `create-pass-form-submit`
        "
        class="d-inline"
        @click="submit()"
      >
        <CLoadingButton
          :spinner="isLoading"
          :disabled="isLoading"
          :loading="isLoading"
          class="mt-2 btn bg-gradient-blue text-white"
        >
          Submit
        </CLoadingButton>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core"
import { required } from "vuelidate/lib/validators"
import FavoriteCarousel from "@/v3components/FavoriteCarousel.vue"
import LazyLoadSelect from "@/components/LazyLoadSelect.vue"

export default {
  name: "CreatePassForm",
  components: {
    FavoriteCarousel,
    LazyLoadSelect
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: false,
      isCapacityReached: false,
      form: {
        from: [],
        to: [],
        reason: null
      },
      responseMessage: {
        type: "",
        message: "",
        errors: {}
      }
    }
  },
  validations: {
    form: {
      from: {
        required
      },
      to: {
        required,
        available: (value, vm) => {
          if (vm.to && vm.to.value && !vm.to.value.unavailability) {
            return true
          }
          return false
        },
        //capacityLimit:(value, vm) => {if((vm.to && vm.to.value) && !(vm.to.hasOwnProperty('capacity') && vm.to.capacity && vm.to.capacity.capacity_reached)){return true} return false;},
        notSameAsFrom: (value, vm) => {
          if (!(vm.from && vm.from.value) && vm.to && vm.to.value) {
            return true
          }
          if (!(vm.from && vm.from.value) || !(vm.to && vm.to.value)) {
            return false
          }
          if (vm.from.value.type != vm.to.value.type) {
            return true
          }

          return vm.from.value.id != vm.to.value.id
        }
      },
      reason: {
        mandatory: (value, vm) => {
          if (
            vm.to &&
            vm.to.value &&
            vm.to.value.comment_type &&
            vm.to.value.comment_type == "Mandatory"
          ) {
            if (vm.reason && vm.reason != "" && vm.reason != " ") {
              return true
            } else {
              return false
            }
          } else {
            return true
          }
        }
      }
    }
  },
  computed: {
    isFormValid() {
      return !this.v$.form.$invalid
    },
    toCapacityReached() {
      return this.form && this.form.to && this.isCapacityReached
    }
  },
  watch: {
    form: {
      handler: function () {
        if (this.responseMessage.message) {
          this.resetResponseMessage()
        }
      },
      deep: true
    }
  },
  methods: {
    onChangeEvent(value) {
      this.form.to = value
      if (value.value.type === "App\\Models\\Room") {
        this.$store
          .dispatch("rooms/getCapacityReached", value.value.id)
          .then((response) => {
            if (response.data) {
              const data = response.data.data
              this.isCapacityReached =
                data.limit !== "-" ? data.capacity_reached : false
            }
          })
      }

      this.$store
        .dispatch("rooms/getUnavailability", {
          id: value.value.id,
          type: value.value.type
        })
        .then((response) => {
          if (response.data.data) {
            this.form.to.value.unavailability = response.data.data
          }
        })
    },
    checkIfValid(fieldName) {
      const field = this.v$.form[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === "")
    },
    submit() {
      this.resetResponseMessage()
      if (this.isFormValid) {
        const pass = {
          from_id: this.form.from.value.id,
          from_type: this.form.from.value.type,
          to_id: this.form.to.value.id,
          to_type: this.form.to.value.type,
          comment: this.form.reason
        }
        this.isLoading = true
        this.$store
          .dispatch("passes/createPass", pass)
          .then((response) => {
            this.responseMessage = {
              type: "success",
              message: "Successfully created."
            }

            setTimeout(() => {
              this.$router.push("/passes/activepass")
              this.isLoading = false
            }, 500)

            if (response.data && response.data.data) {
              const data = response.data.data
              const params = {
                from_id: data.from_id,
                from_type: data.from_type,
                to_id: data.to_id,
                to_type: data.to_type
              }
              this.$store.dispatch("passes/getActiveFlags", params)
            }
          })
          .catch((err) => {
            this.isLoading = false
            const response = err.response.data
            this.setErrorResponse(response.message, response.errors)
          })
      } else {
        this.v$.$touch()
      }
    },
    resetResponseMessage() {
      this.responseMessage = {
        type: "",
        message: "",
        errors: {}
      }
    },
    setErrorResponse(message, errors) {
      this.responseMessage = {
        type: "danger",
        message:
          message && message !== "The given data was invalid." ? message : "",
        errors: errors ? errors : {}
      }
    }
  }
}
</script>

<style></style>
